import { useEffect, useMemo, useRef, useState } from 'react';
import { useGameStore } from '@/ui/store/gameStore';
import { eventBus } from '@/shared/EventBus';
import { Icon } from './Icon';
import { PetCard } from './PetCard';
import {
  PetCollectionFilters,
  PetMergeProgressBadge,
  PET_SORT_OPTIONS_BASIC,
} from './PetCollectionFilters';
import { formatNumberCompact } from '@/utils/formatNumber';
import {
  usePetCollectionFilters,
  usePetCollectionView,
} from '@/ui/hooks/usePetCollectionView';
import { EGGS_BY_ID } from '@/config/eggs.config';
import { eggAssetUrl } from '@/assets/paths';
import { useFreeEggCounts } from '@/ui/hooks/useFreeEggCounts';
import type { PetInstance } from '@/domain/types/pet';
import type { Variant } from '@/domain/types/common';
import type { TradeOfferPet, TradeOfferEgg } from '@/net/plazaRoom';

// Parity z trading-plaza.config.json `eggTrade` (E-5) i realtime.
const MAX_PETS_PER_SIDE = 6;
const MAX_EGG_TYPES_PER_SIDE = 4;
const MAX_EGGS_PER_SIDE = 24;

function sameEggQuantities(a: ReadonlyMap<string, number>, b: ReadonlyMap<string, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [defId, quantity] of a) {
    if (b.get(defId) !== quantity) return false;
  }
  return true;
}

/**
 * DirectTradeModal — handel bezpośredni 1-na-1 (5.C.1 handshake + 5.C.2 oferty
 * + 5.C.3 lock/confirm + 5.C.4 atomic + 5.C.5 polish UI).
 *
 * Fazy (z `store.trade`): `invite` (Tak/Nie), `pending` (czekam), `active` (okno
 * wymiany — dwie półki z petami, jajkami i gemami, picker mojego ekwipunku),
 * `completed` (overlay sukcesu ~1.5 s zanim zamkniemy — polish 5.C.5).
 *
 * Oferty są SERVER-AUTHORITATIVE: klik peta/zmiana gemów → event do PlazaScene →
 * realtime waliduje (własność/limit/saldo) → `trade_state` → store. Lokalny
 * draft steppera jajek daje szybką reakcję na dotyk, ale stan serwera pozostaje
 * źródłem prawdy. Lock/confirm = 5.C.3 (anti-scam: lock resetuje się przy
 * zmianie oferty); atomowa wymiana = 5.C.4 (API).
 *
 * IKONY: zgodnie z `feedback_no_emoji_in_ui` UI NIE używa inline emoji —
 * wszystko przez `<Icon src="/ui/*.webp" fallback="...">` (fallback emoji tylko
 * na wypadek braku PNG, normalnie nigdy nie widoczny).
 */
export function DirectTradeModal(): JSX.Element | null {
  const trade = useGameStore((s) => s.trade);
  if (!trade) return null;
  if (trade.phase === 'completed') return <CompletedTrade peerName={trade.peerName} />;
  if (trade.phase === 'active') return <ActiveTrade tradeId={trade.tradeId} peerName={trade.peerName} />;

  const { tradeId, peerName } = trade;
  const accept = (): void => eventBus.emit('trade:accept', { tradeId });
  const decline = (): void => eventBus.emit('trade:decline', { tradeId });
  const cancel = (): void => eventBus.emit('trade:cancel', { tradeId });

  return (
    <Shell peerName={peerName}>
      {trade.phase === 'invite' && (
        <>
          <p className="text-sm text-gray-700 mb-4">
            <span className="font-bold text-meadow-700">{peerName}</span> chce z Tobą
            handlować. Otworzyć okno wymiany?
          </p>
          <div className="flex gap-2">
            <button
              onClick={accept}
              className="flex-1 bg-meadow-500 hover:bg-meadow-700 text-white rounded-xl py-2.5 font-bold active:scale-95 transition inline-flex items-center justify-center gap-1"
            >
              <Icon src="/ui/ui_btn_confirm.webp" fallback="✓" alt="tak" className="w-5 h-5" />
              Tak, handlujmy
            </button>
            <button
              onClick={decline}
              className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl py-2.5 font-bold active:scale-95 transition"
            >
              Nie teraz
            </button>
          </div>
        </>
      )}

      {trade.phase === 'pending' && (
        <>
          <p className="text-sm text-gray-600 mb-4 inline-flex items-center gap-1.5">
            <Icon src="/ui/ui_hourglass.webp" fallback="⏳" alt="czekam" className="w-4 h-4" />
            Czekam na akceptację od <span className="font-bold text-meadow-700">{peerName}</span>…
          </p>
          <button
            onClick={cancel}
            className="w-full bg-red-100 hover:bg-red-200 text-red-700 rounded-xl py-2.5 font-bold active:scale-95 transition"
          >
            Anuluj zaproszenie
          </button>
        </>
      )}
    </Shell>
  );
}

/**
 * Polish 5.C.5: celebration overlay zamiast natychmiastowego zamknięcia modala
 * po wymianie. Wyświetlany ~1.5 s (timer trzyma PlazaScene.onTradeCompleted),
 * potem store ustawia `trade=null` i komponent znika.
 */
function CompletedTrade({ peerName }: { peerName: string }): JSX.Element {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-meadow-700/30 backdrop-blur-sm pointer-events-auto font-game">
      <div className="bg-white rounded-3xl p-6 shadow-2xl w-full max-w-md mx-4 text-center animate-trade-pop">
        <Icon
          src="/ui/ui_trade_success.webp"
          fallback="🎉"
          alt="sukces"
          className="w-24 h-24 mx-auto mb-2"
        />
        <h2 className="text-2xl font-bold text-meadow-700 mb-1">Wymiana zakończona!</h2>
        <p className="text-sm text-gray-600">
          Pomyślnie wymieniłeś się z{' '}
          <span className="font-bold text-meadow-700">{peerName}</span>.
        </p>
      </div>
    </div>
  );
}

/** Wspólny kontener modala handlu. */
function Shell({ peerName, children }: { peerName: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-meadow-700/30 backdrop-blur-sm pointer-events-auto font-game">
      <div className="bg-white rounded-3xl p-5 shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-meadow-700 inline-flex items-center gap-2 mb-3">
          <Icon src="/ui/ui_btn_trade.webp" fallback="🤝" alt="handel" className="w-7 h-7" />
          Handel z {peerName}
        </h2>
        {children}
      </div>
    </div>
  );
}

/** Faza aktywna — dwie półki ofert + picker petów/jajek + gemy (5.C.2). */
function ActiveTrade({ tradeId, peerName }: { tradeId: string; peerName: string }): JSX.Element {
  const myOffer = useGameStore((s) => s.tradeMyOffer);
  const theirOffer = useGameStore((s) => s.tradeTheirOffer);
  const petInventory = useGameStore((s) => s.petInventory);
  const equippedUids = useGameStore((s) => s.equippedUids);
  const gems = useGameStore((s) => s.gems);
  const { filters, patchFilters, resetFilters } = usePetCollectionFilters({ sortBy: 'power' });
  const [mobileTab, setMobileTab] = useState<'add' | 'mine' | 'theirs'>('add');
  // Pety i jajka są równorzędnymi kategoriami dodawania. Nie renderujemy ich
  // jeden pod drugim: na mobile sekcja jajek znikała wcześniej pod długim
  // pickerem petów, przez co wyglądała jak doklejona funkcja.
  const [pickerTab, setPickerTab] = useState<'pets' | 'eggs'>('pets');

  const myLocked = myOffer.locked;
  const bothLocked = myOffer.locked && theirOffer.locked;
  const bothConfirmed = myOffer.confirmed && theirOffer.confirmed;

  const cancel = (): void => eventBus.emit('trade:cancel', { tradeId });
  // Po zablokowaniu mojej oferty NIE wolno jej edytować (anti-scam). Najpierw
  // „Odblokuj", co po stronie serwera kasuje też confirmy obu stron.
  const addPet = (petUid: string): void => {
    if (myLocked) return;
    eventBus.emit('trade:addPet', { tradeId, petUid });
  };
  const removePet = (petUid: string): void => {
    if (myLocked) return;
    eventBus.emit('trade:removePet', { tradeId, petUid });
  };
  const lock = (): void => eventBus.emit('trade:lock', { tradeId });
  const unlock = (): void => eventBus.emit('trade:unlock', { tradeId });
  const confirm = (): void => eventBus.emit('trade:confirm', { tradeId });

  // E-5: jajka w ofercie. `free` = wolne sztuki (bez wystawionych w budce) —
  // ten sam rachunek co ekwipunek. Serwer i tak clampuje, ale pokazujemy realne
  // „masz N", żeby dziecko nie próbowało oddać jajka, które wisi na sprzedaż.
  // `failed` (Codex E-5 LOW 1): gdy nie wiemy, co jest wystawione, NIE wolno
  // proponować dodania — inaczej gracz klika jajko z budki i „przycisk nie działa"
  // (realtime je odrzuci). Blokujemy tylko DODAWANIE; usuwanie z oferty zostaje.
  const { free: freeEggs, failed: eggsFailed, reload: reloadEggs } = useFreeEggCounts();
  const serverOfferedEggs = useMemo(
    () => new Map(myOffer.eggs.map((e) => [e.defId, e.quantity])),
    [myOffer.eggs],
  );
  /**
   * Lokalny draft ilości jajek. Jedno jajko = jedna instancja i jedno miejsce
   * w limicie 24, ale wiele sztuk tego samego typu pokazujemy jako stos ×N.
   *
   * Draft daje natychmiastową reakcję steppera: szybkie +/+ wysyła 1, potem 2,
   * zamiast dwa razy wartości 1 oczekującej na round-trip realtime. Serwer nadal
   * jest źródłem prawdy; po `trade_state` uzgadniamy draft z zaakceptowaną ofertą.
   */
  const [offeredEggs, setOfferedEggs] = useState<Map<string, number>>(
    () => new Map(serverOfferedEggs),
  );
  const pendingEggTargets = useRef(new Map<string, number>());
  useEffect(() => {
    setOfferedEggs((current) => {
      const next = new Map(serverOfferedEggs);
      for (const [defId, target] of pendingEggTargets.current) {
        const accepted = serverOfferedEggs.get(defId) ?? 0;
        if (accepted === target) {
          pendingEggTargets.current.delete(defId);
        } else if (target > 0) {
          // Odpowiedź innego typu mogła przyjść wcześniej. Nie cofaj lokalnego
          // steppera tego wiersza, dopóki serwer nie odeśle jego wartości.
          next.set(defId, target);
        } else {
          next.delete(defId);
        }
      }
      // `trade_state` może dotyczyć wyłącznie ruchu drugiego gracza. Wtedy nie
      // twórz nowej Mapy i nie wywołuj zbędnego renderu całego pickera.
      return sameEggQuantities(current, next) ? current : next;
    });
  }, [serverOfferedEggs]);
  // Rodzaje, które gracz w ogóle POSIADA wolne — posortowane po nazwie, żeby
  // lista nie skakała między renderami.
  const ownedEggTypes = useMemo(
    () =>
      Object.entries(freeEggs)
        .filter(([, n]) => n > 0)
        .map(([defId]) => defId)
        .filter((defId) => EGGS_BY_ID[defId])
        .sort((x, y) => (EGGS_BY_ID[x]!.name.pl).localeCompare(EGGS_BY_ID[y]!.name.pl, 'pl')),
    [freeEggs],
  );
  const offeredEggTotal = useMemo(
    () => [...offeredEggs.values()].reduce((sum, quantity) => sum + quantity, 0),
    [offeredEggs],
  );
  const setEggs = (defId: string, quantity: number): void => {
    if (myLocked) return;
    const nextQuantity = Math.max(0, Math.floor(quantity));
    pendingEggTargets.current.set(defId, nextQuantity);
    setOfferedEggs((current) => {
      const next = new Map(current);
      if (nextQuantity === 0) next.delete(defId);
      else next.set(defId, nextQuantity);
      return next;
    });
    eventBus.emit('trade:setEggs', { tradeId, defId, quantity: nextQuantity });
  };

  // Lokalny input gemów — emituje do serwera (serwer clampuje do salda).
  const [crystalsInput, setCrystalsInput] = useState(String(myOffer.crystals));
  // Synchronizuj z wartością ZAAKCEPTOWANĄ przez serwer (Codex 5.C.2 LOW 1) —
  // gdy serwer zclampuje (np. >saldo), input pokazuje realną kwotę z oferty.
  useEffect(() => {
    setCrystalsInput(String(myOffer.crystals));
  }, [myOffer.crystals]);
  const onCrystals = (v: string): void => {
    if (myLocked) return;
    setCrystalsInput(v);
    const n = parseInt(v, 10);
    eventBus.emit('trade:setCrystals', { tradeId, crystals: Number.isFinite(n) ? Math.max(0, n) : 0 });
  };

  const offeredUids = useMemo(() => new Set(myOffer.pets.map((p) => p.uid)), [myOffer.pets]);
  const equippedSet = useMemo(() => new Set(equippedUids), [equippedUids]);
  const tradeCounted = useMemo(
    () => (p: PetInstance) =>
      !p.locked && !p.favorite && !equippedSet.has(p.uid) && !offeredUids.has(p.uid),
    [equippedSet, offeredUids],
  );
  // Pety do zaoferowania: nie locked/favorite/equipped, nie już na półce.
  const eligible = useMemo(
    () => petInventory.filter(tradeCounted),
    [petInventory, tradeCounted],
  );
  const pickerView = usePetCollectionView({
    items: eligible,
    filters,
    mergeCountPets: petInventory,
    mergeCounted: tradeCounted,
  });

  return (
    <Shell peerName={peerName}>
      <div className="sm:hidden mb-3 grid grid-cols-3 gap-1.5">
        <button
          type="button"
          onClick={() => setMobileTab('add')}
          className={`inline-flex items-center justify-center gap-1 rounded-xl px-1 py-2 text-xs font-bold leading-tight active:scale-95 ${
            mobileTab === 'add'
              ? 'bg-meadow-500 text-white'
              : 'bg-meadow-50 text-meadow-700'
          }`}
        >
          <Icon src="/ui/ui_btn_plus.webp" fallback="+" alt="" className="w-4 h-4" />
          Dodaj
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('mine')}
          className={`rounded-xl px-1 py-2 text-xs font-bold leading-tight active:scale-95 ${
            mobileTab === 'mine'
              ? 'bg-meadow-500 text-white'
              : 'bg-meadow-50 text-meadow-700'
          }`}
        >
          Moja oferta
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('theirs')}
          className={`rounded-xl px-1 py-2 text-xs font-bold leading-tight active:scale-95 ${
            mobileTab === 'theirs'
              ? 'bg-meadow-500 text-white'
              : 'bg-meadow-50 text-meadow-700'
          }`}
        >
          Druga strona
        </button>
      </div>

      {/* Dwie półki ofert. Polish 5.C.5: półka peer'a flashuje gdy zmieni
          ofertę — inaczej zmiana cicho aktualizuje DOM i można jej nie zauważyć. */}
      <div className="mb-3">
        <div className={`${mobileTab === 'mine' ? 'block' : 'hidden'} sm:hidden`}>
          <OfferShelf title="Twoja oferta" offer={myOffer} onRemovePet={removePet} mine />
        </div>
        <div className={`${mobileTab === 'theirs' ? 'block' : 'hidden'} sm:hidden`}>
          <OfferShelf title={peerName} offer={theirOffer} flashOnChange />
        </div>
        <div className="hidden sm:grid sm:grid-cols-2 gap-3">
          <OfferShelf title="Twoja oferta" offer={myOffer} onRemovePet={removePet} mine />
          <OfferShelf title={peerName} offer={theirOffer} flashOnChange />
        </div>
      </div>

      <div className={`${mobileTab === 'add' ? 'block' : 'hidden'} sm:block`}>
        {/* Moje gemy do oferty */}
        <div className="bg-meadow-50 rounded-xl p-2.5 flex flex-wrap items-center gap-2 mb-3 text-sm">
          <span className="font-bold text-meadow-700 inline-flex items-center gap-1">
            Twoje
            <Icon src="/ui/ui_gem.webp" fallback="💎" alt="gem" className="w-4 h-4" />
            w ofercie:
          </span>
          <input
            type="number"
            min={0}
            max={gems}
            value={crystalsInput}
            disabled={myLocked}
            onChange={(e) => onCrystals(e.target.value)}
            className="w-24 px-3 py-1.5 rounded-xl border-2 border-meadow-200 focus:border-meadow-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
          />
          <span className="text-xs text-gray-500 inline-flex items-center gap-1">
            (masz {formatNumberCompact(gems)}
            <Icon src="/ui/ui_gem.webp" fallback="💎" alt="gem" className="w-3 h-3" />)
          </span>
        </div>

        {/* Picker: pety i jajka są równorzędnymi kategoriami. Gemy zostają
            osobno nad nimi jako dopłata do dowolnej kombinacji przedmiotów. */}
        {myLocked ? (
          <p className="text-xs text-meadow-700 bg-meadow-50 rounded-xl px-3 py-2 mb-3 inline-flex items-center gap-1.5">
            <Icon src="/ui/ui_btn_lock.webp" fallback="🔒" alt="zablokowane" className="w-4 h-4" />
            Twoja oferta jest zablokowana. Odblokuj, aby coś zmienić.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3" role="tablist" aria-label="Co dodajesz do oferty">
              <button
                id="trade-picker-pets-tab"
                type="button"
                role="tab"
                aria-controls="trade-picker-pets-panel"
                aria-selected={pickerTab === 'pets'}
                onClick={() => setPickerTab('pets')}
                className={`min-h-12 rounded-xl border-2 px-2 py-2 inline-flex items-center justify-center gap-2 font-bold active:scale-95 transition ${
                  pickerTab === 'pets'
                    ? 'border-meadow-500 bg-meadow-100 text-meadow-800 shadow-sm'
                    : 'border-meadow-100 bg-white text-meadow-700'
                }`}
              >
                <Icon src="/ui/ui_btn_pets.webp" fallback="🐾" alt="" className="w-7 h-7" />
                <span className="text-sm">Pety</span>
                <span className="rounded-full bg-white/90 px-2 py-0.5 text-xs tabular-nums">
                  {myOffer.pets.length}/{MAX_PETS_PER_SIDE}
                </span>
              </button>
              <button
                id="trade-picker-eggs-tab"
                type="button"
                role="tab"
                aria-controls="trade-picker-eggs-panel"
                aria-selected={pickerTab === 'eggs'}
                onClick={() => setPickerTab('eggs')}
                className={`min-h-12 rounded-xl border-2 px-2 py-2 inline-flex items-center justify-center gap-2 font-bold active:scale-95 transition ${
                  pickerTab === 'eggs'
                    ? 'border-yellow-400 bg-yellow-50 text-yellow-800 shadow-sm'
                    : 'border-meadow-100 bg-white text-meadow-700'
                }`}
              >
                <Icon src="/ui/ui_btn_eggs.webp" fallback="🥚" alt="" className="w-7 h-7" />
                <span className="text-sm">Jajka</span>
                <span className="rounded-full bg-white/90 px-2 py-0.5 text-xs tabular-nums">
                  {offeredEggTotal}/{MAX_EGGS_PER_SIDE}
                </span>
              </button>
            </div>

            {pickerTab === 'pets' ? (
              <div id="trade-picker-pets-panel" role="tabpanel" aria-labelledby="trade-picker-pets-tab">
                <h3 className="text-sm font-bold text-meadow-700 mb-1.5">Dodaj peta do oferty</h3>
                {eligible.length === 0 ? (
                  <p className="text-xs text-gray-500 mb-3">
                    Brak petów do zaoferowania (ulubione, zablokowane, wyposażone i już
                    dodane są pomijane).
                  </p>
                ) : (
                  <>
                    <PetCollectionFilters
                      filters={filters}
                      onChange={(patch) => patchFilters(patch)}
                      onReset={resetFilters}
                      totalCount={eligible.length}
                      visibleCount={pickerView.visible.length}
                      sortOptions={PET_SORT_OPTIONS_BASIC}
                      originCounts={pickerView.originCounts}
                    />
                    {pickerView.visible.length === 0 ? (
                      <p className="text-xs text-gray-500 mb-3">Brak petów spełniających filtry.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[34vh] overflow-y-auto mb-3">
                        {pickerView.visible.map((p) => (
                          <div key={p.uid} className="relative">
                            <PetCard pet={p} compact onClick={() => addPet(p.uid)} />
                            <PetMergeProgressBadge
                              progress={pickerView.mergeProgressForItem(p)}
                              active={filters.mergeMode !== 'all'}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div id="trade-picker-eggs-panel" role="tabpanel" aria-labelledby="trade-picker-eggs-tab">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-1">
                  <h3 className="text-sm font-bold text-meadow-700">Dodaj jajka do oferty</h3>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {offeredEggTotal}/{MAX_EGGS_PER_SIDE} jajek · {offeredEggs.size}/{MAX_EGG_TYPES_PER_SIDE} rodzajów
                  </span>
                </div>
                <p className="mb-2 text-[11px] leading-tight text-gray-500">
                  Każda sztuka zajmuje jedno miejsce. Takie same jajka pokażemy w ofercie jako jeden stos ×N.
                </p>
                {eggsFailed && (
                  <div className="mb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs bg-amber-50 text-amber-700 rounded-xl px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon src="/ui/ui_warning.webp" fallback="⚠" alt="" className="w-4 h-4 shrink-0" />
                      Nie udało się sprawdzić jajek w budce — dodawanie jest chwilowo wstrzymane.
                    </span>
                    <button
                      type="button"
                      onClick={reloadEggs}
                      className="min-h-10 shrink-0 rounded-lg bg-amber-200 px-3 py-1 font-bold text-amber-800 active:scale-95"
                    >
                      Spróbuj ponownie
                    </button>
                  </div>
                )}
                {ownedEggTypes.length === 0 ? (
                  <p className="text-xs text-gray-500 mb-3 rounded-xl bg-meadow-50 px-3 py-6 text-center">
                    Nie masz wolnych jajek. Sztuki wystawione w budce są pomijane.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto mb-3 pr-0.5">
                    {ownedEggTypes.map((defId) => {
                      const eggDef = EGGS_BY_ID[defId]!;
                      const owned = freeEggs[defId] ?? 0;
                      const inOffer = offeredEggs.get(defId) ?? 0;
                      const newTypeBlocked = inOffer === 0 && offeredEggs.size >= MAX_EGG_TYPES_PER_SIDE;
                      const otherEggs = offeredEggTotal - inOffer;
                      const selectableMax = newTypeBlocked
                        ? 0
                        : Math.max(0, Math.min(owned, MAX_EGGS_PER_SIDE - otherEggs));
                      const plusDisabled = eggsFailed || inOffer >= selectableMax;
                      const maxDisabled = eggsFailed || selectableMax === 0 || inOffer === selectableMax;
                      return (
                        <div
                          key={defId}
                          className={`rounded-2xl border-2 p-2.5 transition ${
                            inOffer > 0
                              ? 'border-yellow-300 bg-yellow-50/70 shadow-sm'
                              : 'border-meadow-100 bg-meadow-50/60'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <img
                              src={eggAssetUrl(eggDef.art.textureKey)}
                              alt={eggDef.name.pl}
                              className="w-12 h-12 object-contain shrink-0 drop-shadow-sm"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-bold text-meadow-800 truncate" title={eggDef.name.pl}>
                                {eggDef.name.pl}
                              </p>
                              <p className="text-xs text-gray-500">Masz wolnych: {owned}</p>
                            </div>
                            {inOffer > 0 && (
                              <span className="shrink-0 rounded-full bg-yellow-200 px-2 py-1 text-sm font-extrabold text-yellow-900 tabular-nums">
                                ×{inOffer}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEggs(defId, inOffer - 1)}
                              disabled={inOffer === 0}
                              className="w-11 h-11 shrink-0 rounded-xl bg-white border-2 border-meadow-200 text-xl text-meadow-700 font-bold disabled:opacity-35 active:scale-95"
                              aria-label={`Mniej ${eggDef.name.pl}`}
                            >
                              −
                            </button>
                            <span
                              className="min-w-10 flex-1 text-center text-lg font-extrabold text-meadow-800 tabular-nums"
                              aria-live="polite"
                            >
                              {inOffer}
                            </span>
                            <button
                              type="button"
                              onClick={() => setEggs(defId, inOffer + 1)}
                              disabled={plusDisabled}
                              className="w-11 h-11 shrink-0 rounded-xl bg-white border-2 border-meadow-300 text-xl text-meadow-700 font-bold disabled:opacity-35 active:scale-95"
                              aria-label={`Więcej ${eggDef.name.pl}`}
                            >
                              +
                            </button>
                            <button
                              type="button"
                              onClick={() => setEggs(defId, selectableMax)}
                              disabled={maxDisabled}
                              className="min-h-11 shrink-0 rounded-xl bg-meadow-100 px-3 text-xs font-extrabold text-meadow-800 disabled:opacity-35 active:scale-95"
                              aria-label={`Dodaj maksymalną liczbę ${eggDef.name.pl}`}
                            >
                              MAX
                            </button>
                          </div>
                          {newTypeBlocked && (
                            <p className="mt-1.5 text-[11px] font-bold text-amber-700">
                              Limit {MAX_EGG_TYPES_PER_SIDE} rodzajów — usuń najpierw inny stos.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Status drugiej strony */}
      <div className="text-xs text-center mb-2 font-bold inline-flex items-center justify-center w-full gap-1.5">
        {theirOffer.confirmed ? (
          <span className="text-meadow-700 inline-flex items-center gap-1">
            <Icon src="/ui/ui_btn_confirm.webp" fallback="✓" alt="potwierdzono" className="w-4 h-4" />
            {peerName} potwierdził wymianę.
          </span>
        ) : theirOffer.locked ? (
          <span className="text-amber-600 inline-flex items-center gap-1">
            <Icon src="/ui/ui_btn_lock.webp" fallback="🔒" alt="zablokowane" className="w-4 h-4" />
            {peerName} zablokował ofertę.
          </span>
        ) : (
          <span className="text-gray-400 inline-flex items-center gap-1">
            <Icon src="/ui/ui_hourglass.webp" fallback="⏳" alt="czekam" className="w-4 h-4" />
            {peerName} jeszcze edytuje ofertę…
          </span>
        )}
      </div>

      {/* Lock / Confirm / Cancel */}
      <div className="sticky bottom-0 grid grid-cols-2 gap-2 bg-white pt-2 shadow-[0_-8px_16px_rgba(255,255,255,0.9)]">
        {myLocked ? (
          <button
            onClick={unlock}
            className="bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-xl py-2.5 font-bold active:scale-95 transition inline-flex items-center justify-center gap-1"
          >
            <Icon src="/ui/ui_btn_unlock.webp" fallback="✏" alt="odblokuj" className="w-5 h-5" />
            <span className="sm:hidden">Odblokuj</span>
            <span className="hidden sm:inline">Odblokuj ofertę</span>
          </button>
        ) : (
          <button
            onClick={lock}
            className="bg-meadow-100 hover:bg-meadow-200 text-meadow-700 rounded-xl py-2.5 font-bold active:scale-95 transition inline-flex items-center justify-center gap-1"
          >
            <Icon src="/ui/ui_btn_lock.webp" fallback="🔒" alt="zablokuj" className="w-5 h-5" />
            <span className="sm:hidden">Zablokuj</span>
            <span className="hidden sm:inline">Zablokuj ofertę</span>
          </button>
        )}
        <button
          onClick={confirm}
          disabled={!bothLocked || myOffer.confirmed}
          className="bg-meadow-500 hover:bg-meadow-700 text-white rounded-xl py-2.5 font-bold active:scale-95 transition disabled:bg-gray-200 disabled:text-gray-400 disabled:active:scale-100 inline-flex items-center justify-center gap-1"
        >
          <Icon src="/ui/ui_btn_confirm.webp" fallback="✓" alt="potwierdź" className="w-5 h-5" />
          {/* Polish 5.C.5: gdy JA potwierdziłem ale druga strona jeszcze nie,
              „Potwierdzono" sugeruje że wymiana się odbyła. Pokazujemy
              czytelny stan oczekiwania zamiast samego ptaszka. */}
          {myOffer.confirmed ? (
            bothConfirmed ? 'Potwierdzono' : (
              <>
                <span className="sm:hidden">Czekam…</span>
                <span className="hidden sm:inline">Czekam na drugą stronę…</span>
              </>
            )
          ) : (
            <>
              <span className="sm:hidden">Potwierdź</span>
              <span className="hidden sm:inline">Potwierdź wymianę</span>
            </>
          )}
        </button>
      </div>
      <p className="text-[11px] text-gray-400 text-center mt-1.5 inline-flex items-center justify-center w-full gap-1">
        {bothConfirmed ? (
          <>
            <Icon src="/ui/ui_hourglass.webp" fallback="⏳" alt="finalizowanie" className="w-3 h-3" />
            Finalizowanie wymiany…
          </>
        ) : bothLocked ? (
          'Obie strony zablokowane — potwierdźcie, aby sfinalizować wymianę.'
        ) : (
          'Najpierw obie strony muszą zablokować swoją ofertę.'
        )}
      </p>

      <button
        onClick={cancel}
        disabled={bothConfirmed}
        className="w-full mt-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl py-2.5 font-bold active:scale-95 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:active:scale-100"
      >
        {bothConfirmed ? 'Wymiana w toku…' : 'Zakończ handel'}
      </button>
    </Shell>
  );
}

/** Hash oferty do detekcji zmiany (flash peer offer w 5.C.5 polish). */
function offerHash(o: { pets: readonly TradeOfferPet[]; crystals: number; eggs: readonly TradeOfferEgg[] }): string {
  const eggs = o.eggs.map((e) => `${e.defId}:${e.quantity}`).join(',');
  return `${o.crystals}|${o.pets.map((p) => p.uid).join(',')}|${eggs}`;
}

/** Półka oferty (pety + jajka jako ikony + gemy). `mine` = pety klikalne (zdejmij). */
function OfferShelf({
  title,
  offer,
  onRemovePet,
  mine = false,
  flashOnChange = false,
}: {
  title: string;
  offer: {
    pets: readonly TradeOfferPet[];
    crystals: number;
    eggs: readonly TradeOfferEgg[];
    locked: boolean;
    confirmed: boolean;
  };
  onRemovePet?: (uid: string) => void;
  mine?: boolean;
  flashOnChange?: boolean;
}): JSX.Element {
  // Polish 5.C.5: ring-flash gdy peer zmieni skład oferty (pety lub gemy).
  // Locked/confirmed świadomie wyłączone z hasha — te już mają osobny stan
  // wizualny (badge + kolor ramki).
  const [flash, setFlash] = useState(false);
  const hash = offerHash(offer);
  const eggCount = offer.eggs.reduce((sum, egg) => sum + egg.quantity, 0);
  const prevHash = useRef(hash);
  useEffect(() => {
    if (!flashOnChange) return;
    if (prevHash.current === hash) return;
    prevHash.current = hash;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 600);
    return () => clearTimeout(t);
  }, [hash, flashOnChange]);

  const lockBorder = offer.confirmed
    ? 'border-meadow-500 bg-meadow-50'
    : offer.locked
      ? 'border-amber-300 bg-amber-50/40'
      : mine
        ? 'border-meadow-300 bg-meadow-50/50'
        : 'border-gray-200 bg-gray-50/50';
  const flashRing = flash ? 'ring-4 ring-amber-300/70' : 'ring-0 ring-transparent';
  return (
    <div className={`rounded-2xl border-2 p-2 transition-shadow duration-300 ${lockBorder} ${flashRing}`}>
      <div className="text-xs font-bold text-meadow-700 mb-1.5 truncate flex items-center justify-between">
        <span className="truncate">{title}</span>
        {offer.confirmed ? (
          <Icon src="/ui/ui_btn_confirm.webp" fallback="✓" alt="potwierdzono" className="w-4 h-4 shrink-0" />
        ) : offer.locked ? (
          <Icon src="/ui/ui_btn_lock.webp" fallback="🔒" alt="zablokowano" className="w-4 h-4 shrink-0" />
        ) : null}
      </div>
      {offer.pets.length === 0 && offer.crystals === 0 && offer.eggs.length === 0 ? (
        <p className="text-[11px] text-gray-400 py-4 text-center">Pusto</p>
      ) : (
        <>
          {offer.pets.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              {offer.pets.map((p) => {
                const inst: PetInstance = { uid: p.uid, defId: p.defId, variant: p.variant as Variant, acquiredAt: 0 };
                return (
                  <PetCard
                    key={p.uid}
                    pet={inst}
                    compact
                    showStateBadges={false}
                    onClick={mine && onRemovePet ? () => onRemovePet(p.uid) : undefined}
                  />
                );
              })}
            </div>
          )}
          {offer.eggs.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {offer.eggs.map((e) => {
                const eggDef = EGGS_BY_ID[e.defId];
                return (
                  <div
                    key={e.defId}
                    className="min-w-0 flex items-center gap-1.5 bg-yellow-50/90 rounded-xl px-2 py-1.5 border border-yellow-200"
                    title={`${eggDef?.name.pl ?? e.defId}: ${e.quantity} szt.`}
                  >
                    <img
                      src={eggAssetUrl(eggDef?.art.textureKey ?? 'egg_starter')}
                      alt={eggDef?.name.pl ?? e.defId}
                      className="w-9 h-9 object-contain shrink-0 drop-shadow-sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11px] leading-tight font-bold text-meadow-800">
                        {eggDef?.name.pl ?? e.defId}
                      </span>
                      <span className="block text-sm leading-tight font-extrabold text-yellow-900 tabular-nums">
                        ×{e.quantity}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {(offer.pets.length > 0 || eggCount > 0 || offer.crystals > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-meadow-200/70 pt-1.5 text-[11px] font-bold text-gray-500">
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Icon src="/ui/ui_btn_pets.webp" fallback="🐾" alt="pety" className="w-4 h-4" />
            {offer.pets.length}/{MAX_PETS_PER_SIDE}
          </span>
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Icon src="/ui/ui_btn_eggs.webp" fallback="🥚" alt="jajka" className="w-4 h-4" />
            {eggCount}/{MAX_EGGS_PER_SIDE}
          </span>
          <span className={`inline-flex items-center gap-1 tabular-nums ${offer.crystals > 0 ? 'text-purple-700' : ''}`}>
            <Icon src="/ui/ui_gem.webp" fallback="💎" alt="gemy" className="w-4 h-4" />
            {formatNumberCompact(offer.crystals)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * TradeNotice — krótki toast (odrzucono/anulowano/błąd handlu). Auto-znika po
 * ~3.5 s. Renderowany zawsze; sam się chowa gdy `tradeNotice === null`.
 */
export function TradeNotice(): JSX.Element | null {
  const notice = useGameStore((s) => s.tradeNotice);
  const setTradeNotice = useGameStore((s) => s.setTradeNotice);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setTradeNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice, setTradeNotice]);

  if (!notice) return null;
  return (
    <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
      <div className="bg-meadow-700/90 text-white text-sm font-bold rounded-xl px-4 py-2 shadow-lg">
        {notice}
      </div>
    </div>
  );
}
